'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ImageOff } from 'lucide-react';
import { Card, CardContent } from '@/shared/ui/shadcn/card';
import { Badge } from '@/shared/ui/shadcn/badge';
import { RenderProgress } from '@/features/design/render';
import { ErrorBoundary } from '@/shared/ui';
import type { Render } from '@/types/render';
import { RenderStatus } from '@/shared/constants/status';

interface RenderCardProps {
  render: Render;
  className?: string;
  showDetails?: boolean;
  angleId?: string; // Optional - for persisting debug info state
}

export function RenderCard({
  render,
  className = '',
  showDetails = false,
  angleId: _angleId,
}: RenderCardProps) {
  const t = useTranslations('renderCard');
  const [imageError, setImageError] = useState(false);

  const isProcessing =
    render.attributes.status === RenderStatus.PENDING ||
    render.attributes.status === RenderStatus.PROCESSING;
  const isCompleted = render.attributes.status === RenderStatus.COMPLETED;
  const isFailed = render.attributes.status === RenderStatus.FAILED;

  if (isProcessing) {
    return (
      <Card className={className}>
        <CardContent className="p-0">
          <ErrorBoundary>
            <RenderProgress render={render} mode="full" />
          </ErrorBoundary>
        </CardContent>
      </Card>
    );
  }

  if (isFailed) {
    return (
      <Card className={`${className} border-destructive/50 bg-destructive/5`}>
        <CardContent className="p-4">
          <p className="text-list-item-value text-destructive">{t('failedTitle')}</p>
          <p className="text-card-meta text-muted-foreground mt-1">
            {t('failedDescription')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!isCompleted || !render.relationships?.file?.meta?.url) {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <p className="text-list-item-subtitle text-muted-foreground">
            {t('noRenderAvailable')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <div className="relative aspect-[4/3] bg-muted">
        {!imageError ? (
          <Image
            src={render.relationships.file.meta.url}
            alt={t('imageAlt')}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
            className="object-cover rounded-t-lg"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-muted">
            <ImageOff className="w-8 h-8 text-muted-foreground" />
          </div>
        )}

        {/* 4K badge for upscaled renders */}
        {false && /* UPSCALE_HIDDEN */ render.attributes.is_upscaled && (
          <div className="absolute top-2 start-2">
            <Badge
              variant="outline"
              className="bg-transparent text-primary border-primary text-chip px-2 py-0.5 backdrop-blur-sm"
            >
              4K
            </Badge>
          </div>
        )}
      </div>

      {showDetails && (
        <CardContent className="p-4 space-y-2">
          <p className="text-list-item-value">
            {t('renderId', { id: render.id.slice(0, 8) })}
          </p>

          {render.attributes.settings.prompts && (
            <div>
              <p className="text-card-meta text-muted-foreground">
                {t('promptLabel')}
              </p>
              <p className="text-card-meta text-foreground">
                {typeof render.attributes.settings.prompts === 'string'
                  ? render.attributes.settings.prompts
                  : JSON.stringify(render.attributes.settings.prompts)}
              </p>
            </div>
          )}

          {(render.attributes.settings.style ||
            render.attributes.settings.color ||
            render.attributes.settings.material) && (
            <div className="flex gap-2 mt-2">
              {render.attributes.settings.style && (
                <Badge variant="secondary" className="text-chip">
                  {String(render.attributes.settings.style)}
                </Badge>
              )}
              {render.attributes.settings.color && (
                <Badge variant="secondary" className="text-chip">
                  {String(render.attributes.settings.color)}
                </Badge>
              )}
              {render.attributes.settings.material && (
                <Badge variant="secondary" className="text-chip">
                  {String(render.attributes.settings.material)}
                </Badge>
              )}
            </div>
          )}

          {render.attributes.completed_at && (
            <p className="text-card-meta text-muted-foreground">
              {t('completedAt', {
                at: new Date(render.attributes.completed_at).toLocaleString(),
              })}
            </p>
          )}

        </CardContent>
      )}
    </Card>
  );
}
